import {
  durationRegex,
  isValidDuration,
} from '@task-manager/features/task-form/model/schema';

// Re-export so the snooze modal validates against the same grammar the
// task form uses (y/mo/w/d/h/m/s/ms, Jira-style).
export { durationRegex, isValidDuration };

const MS = 1;
const SECOND = 1000 * MS;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
// Calendar-agnostic approximations — match the task-form duration grammar,
// which treats months/years as fixed spans for relative timers.
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

// Ordered longest-first so normalization emits the most significant units.
const UNITS: ReadonlyArray<{ suffix: string; ms: number }> = [
  { suffix: 'y', ms: YEAR },
  { suffix: 'mo', ms: MONTH },
  { suffix: 'w', ms: WEEK },
  { suffix: 'd', ms: DAY },
  { suffix: 'h', ms: HOUR },
  { suffix: 'm', ms: MINUTE },
  { suffix: 's', ms: SECOND },
  { suffix: 'ms', ms: MS },
];

// Capture groups in `durationRegex` align with this order.
const REGEX_UNIT_ORDER: ReadonlyArray<number> = [
  YEAR,
  MONTH,
  WEEK,
  DAY,
  HOUR,
  MINUTE,
  SECOND,
  MS,
];

/**
 * Parse a Jira-style duration string ("1d 5h", "26h", "2w 3d") into total
 * milliseconds. Returns null when the value is empty or doesn't match the
 * shared duration grammar.
 */
export const parseDurationToMs = (value: string): number | null => {
  const trimmed = value.trim();
  if (!isValidDuration(trimmed)) return null;
  const match = durationRegex.exec(trimmed);
  if (!match) return null;
  let total = 0;
  for (let i = 0; i < REGEX_UNIT_ORDER.length; i += 1) {
    const group = match[i + 1];
    if (!group) continue;
    const amount = Number.parseInt(group, 10);
    if (Number.isNaN(amount)) return null;
    total += amount * REGEX_UNIT_ORDER[i];
  }
  return total;
};

/**
 * Render a millisecond span back into a normalized, human-readable string
 * (e.g. 26h → "1d 2h", 90m → "1h 30m"). Carries overflow into larger
 * units so "26h" never stays "26h". Returns "0m" for a zero/empty span.
 */
export const formatMsToDuration = (ms: number): string => {
  if (ms <= 0) return '0m';
  let remaining = ms;
  const parts: string[] = [];
  for (const { suffix, ms: unitMs } of UNITS) {
    if (remaining < unitMs) continue;
    const amount = Math.floor(remaining / unitMs);
    remaining -= amount * unitMs;
    parts.push(`${amount}${suffix}`);
  }
  return parts.length > 0 ? parts.join(' ') : '0m';
};

/**
 * Parse + normalize in one step. Returns null for invalid input.
 */
export const normalizeDuration = (value: string): string | null => {
  const ms = parseDurationToMs(value);
  if (ms === null) return null;
  return formatMsToDuration(ms);
};

/**
 * Compute the absolute snooze target (ISO string) from a relative duration
 * string, anchored to `now`. Returns null for invalid input.
 */
export const computeSnoozedUntilFromDuration = (
  value: string,
  now = new Date()
): string | null => {
  const ms = parseDurationToMs(value);
  if (ms === null || ms <= 0) return null;
  return new Date(now.getTime() + ms).toISOString();
};
